export type FilterOperations = 'AND' | 'OR';

//#region INTERFACE IAdvancedFilterGroup
/**
 * Represents an advanced filter group with filter logic and metadata.
 * @interface IAdvancedFilterGroup
 */
export interface IAdvancedFilterGroup {
  /** The filter group applied to the data. */
  filterGroup: IFilterGroup;
  /** The grid where the filter is applied. */
  grid: string;
  /** The unique identifier for the advanced filter group. */
  id: number;
  /** The name of the advanced filter group. */
  name: string;
  /** Whether the filter group is saved or not. */
  saved: boolean;
  /** Source filter id when copied from another store (cross-store copy idempotency). */
  copiedFromFilterId?: number;
  /** Source store id when this filter was copied from another store (cross-store copy idempotency). */
  copiedFromStoreId?: string;
  /** The last modified date of the advanced filter group (optional). */
  lastModified?: Date;
  /** Id of the General filter this group was migrated from, used to keep migrations idempotent (optional). */
  migratedFromFilterId?: number;
}

//#endregion

//#region INTERFACE IFilterGroup
/**
 * Represents a group of filters or other filter groups.
 * @interface IFilterGroup
 */
export interface IFilterGroup {
  /** The list of filters and nested filter groups. */
  filtersAndGroups: (IFilter | IFilterGroup)[];
  /** The unique identifier for the filter group. */
  id: number;
  /** The logical operator that combines the filters in the group (AND or OR). */
  operator: FilterOperations;
}

//#endregion

//#region INTERFACE IFilter
/**
 * Represents an individual filter applied to a column in the data.
 * @interface IFilter
 */
export interface IFilter {
  /** The column in the data on which the filter is applied. */
  column: string;
  /** The type of the column (e.g., 'string', 'number', 'boolean', 'date'). */
  columnType: string;
  /** The type of the filter operation (e.g., 'equal to', 'greater than'). */
  filterType: string;
  /** The unique identifier for the filter. */
  id: number;
  /** The value to filter by, which can be a string, number, or boolean. */
  value: string | number | boolean;
}

//#endregion
